import { Component, ErrorInfo, ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { ApiError } from "@/lib/api";
import { logoutSession } from "@/lib/auth-session";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Error Boundary específico para errores de autenticación
 * Captura errores relacionados con la autenticación y redirige al login
 */
export class AuthErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    const isAuthError =
      error instanceof ApiError &&
      (error.status === 401 ||
        (error.status === 403 && error.reasonCode !== "MISSING_PERMISSION"));

    return {
      hasError: isAuthError,
      error: isAuthError ? error : undefined,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("AuthErrorBoundary capturó un error:", error, errorInfo);

    // Si es un error de autenticación, limpiar la sesión
    if (this.state.hasError) {
      try {
        logoutSession();
      } catch {
        // ignore cleanup errors
      }
    }
  }

  render() {
    if (this.state.hasError) {
      // Redirigir al login en caso de error de autenticación
      return <Navigate to="/login" replace />;
    }

    return this.props.children;
  }
}

/**
 * Hook para usar el AuthErrorBoundary en componentes funcionales
 */
export const useAuthErrorHandler = () => {
  const handleAuthError = (error: Error) => {
    if (
      error instanceof ApiError &&
      (error.status === 401 ||
        (error.status === 403 && error.reasonCode !== "MISSING_PERMISSION"))
    ) {
      logoutSession();
    }
  };

  return { handleAuthError };
};
